<script setup>
import { computed, ref, watch } from 'vue';
import ApexCharts from 'vue3-apexcharts';
import { useApiData } from '../../composables/useApiData';
import { usePortalAccount } from '../../composables/usePortalAccount';

const props = defineProps({
  widget: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['update:chartType']);
const { accountId } = usePortalAccount();
const { data, loading, error } = useApiData(
  computed(() => props.widget),
  computed(() => accountId.value)
);

const title = computed(() => props.widget.props?.title || 'Graphique');
const chartType = computed(() => props.widget.props?.chartType || 'bar');
const localChartType = ref(chartType.value);

watch(
  () => props.widget.props?.chartType,
  newType => {
    if (newType) {
      localChartType.value = newType;
    }
  }
);

const chartTypeOptions = [
  { value: 'bar', label: 'Barres', icon: 'i-lucide-bar-chart-2' },
  { value: 'line', label: 'Ligne', icon: 'i-lucide-trending-up' },
  { value: 'area', label: 'Aire', icon: 'i-lucide-area-chart' },
  { value: 'donut', label: 'Donut', icon: 'i-lucide-circle' },
];

const categoriesData = computed(() => {
  return (
    data.value?.mapped?.categories ||
    props.widget.props?.categories || [
      'Mon',
      'Tue',
      'Wed',
      'Thu',
      'Fri',
      'Sat',
      'Sun',
    ]
  );
});

const seriesData = computed(() => {
  return (
    data.value?.mapped?.series ||
    props.widget.props?.series || [
      { name: 'Série 1', data: [40, 80, 100, 50, 30, 70, 60] },
    ]
  );
});

// Palette Light Theme
const chartColors = ['#10b981', '#f43f5e', '#6366f1', '#f59e0b', '#8b5cf6'];

const apexChartOptions = computed(() => ({
  chart: {
    type: localChartType.value,
    toolbar: { show: false },
    fontFamily: 'Inter, sans-serif',
    foreColor: '#64748b',
    background: 'transparent',
  },
  colors: chartColors,
  plotOptions: {
    bar: {
      borderRadius: 4,
      borderRadiusApplication: 'around',
      columnWidth: '40%',
    },
  },
  dataLabels: { enabled: false },
  stroke: { show: true, width: 2, curve: 'smooth', lineCap: 'round' },
  xaxis: {
    categories: categoriesData.value,
    axisBorder: { show: false },
    axisTicks: { show: false },
    labels: { style: { colors: '#64748b', fontSize: '10px', fontWeight: 600 } },
  },
  yaxis: {
    labels: { style: { colors: '#64748b', fontSize: '10px', fontWeight: 600 } },
  },
  grid: {
    borderColor: '#f1f5f9',
    strokeDashArray: 4,
    xaxis: { lines: { show: false } },
    yaxis: { lines: { show: true } },
    padding: { top: 0, right: 0, bottom: 0, left: 10 },
  },
  legend: {
    show: true,
    position: 'top',
    horizontalAlign: 'right',
    fontSize: '11px',
    fontWeight: 600,
    labels: { colors: '#475569' },
    itemMargin: { horizontal: 8, vertical: 0 },
  },
  tooltip: {
    theme: 'light',
    style: { fontSize: '11px', fontFamily: 'Inter, sans-serif' },
    x: { show: false },
    marker: { show: true },
  },
  fill: {
    type: 'gradient',
    gradient: {
      shade: 'light',
      type: 'vertical',
      shadeIntensity: 0.5,
      gradientToColors: undefined,
      inverseColors: true,
      opacityFrom: 0.7,
      opacityTo: 0.1,
      stops: [0, 100],
    },
  },
}));

const apexPieOptions = computed(() => ({
  chart: {
    type: 'donut',
    fontFamily: 'Inter, sans-serif',
    background: 'transparent',
  },
  colors: chartColors,
  labels: categoriesData.value,
  stroke: { width: 0 },
  dataLabels: { enabled: false },
  legend: {
    show: true,
    position: 'bottom',
    fontSize: '11px',
    fontWeight: 600,
    labels: { colors: '#475569' },
  },
  plotOptions: {
    pie: {
      donut: {
        size: '70%',
        labels: {
          show: true,
          name: {
            show: true,
            fontSize: '11px',
            fontWeight: 600,
            color: '#64748b',
            offsetY: -6,
          },
          value: {
            show: true,
            fontSize: '20px',
            fontWeight: 800,
            color: '#0f172a',
            offsetY: 6,
            formatter: val => `$${val.toLocaleString()}`,
          },
          total: {
            show: true,
            label: 'TOTAL',
            color: '#64748b',
            formatter: w => {
              const total = w.globals.seriesTotals.reduce((a, b) => a + b, 0);
              return `$${total.toLocaleString()}`;
            },
          },
        },
      },
    },
  },
}));

const isCircular = computed(() => {
  return localChartType.value === 'donut' || localChartType.value === 'pie';
});

const updateChartType = newType => {
  localChartType.value = newType;
  emit('update:chartType', newType);
};
</script>

<template>
  <div
    class="h-full flex flex-col group font-inter p-5 bg-white rounded-[20px] shadow-xl border border-slate-100"
  >
    <div v-if="title" class="mb-4 flex items-center justify-between">
      <div class="flex flex-col gap-0.5">
        <h3 class="text-base font-bold text-slate-900 tracking-tight">
          {{ title }}
        </h3>
        <p
          v-if="seriesData[0]?.data"
          class="text-xs font-bold text-slate-400 tracking-wide mt-0.5"
        >
          Overview
        </p>
      </div>
      <div class="flex items-center">
        <div class="relative group/select">
          <select
            :value="localChartType"
            class="w-full min-w-[160px] appearance-none bg-slate-50 border border-slate-200 text-slate-700 text-sm font-medium rounded-xl px-4 py-2.5 pr-10 hover:bg-white hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all cursor-pointer shadow-sm"
            @change="updateChartType($event.target.value)"
          >
            <option
              v-for="opt in chartTypeOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
          <div
            class="absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none flex items-center justify-center text-slate-400 group-hover/select:text-slate-600 transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9"
              />
            </svg>
          </div>
        </div>
      </div>
    </div>

    <div class="flex-1 min-h-[200px] w-full relative">
      <div
        v-if="loading"
        class="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-white/50 backdrop-blur-sm rounded-xl z-10"
      >
        <div
          class="w-6 h-6 border-2 border-slate-200 border-t-indigo-500 rounded-full animate-spin"
        />
      </div>
      <div
        v-else-if="error"
        class="h-full flex flex-col items-center justify-center p-4 text-center"
      >
        <div
          class="w-10 h-10 rounded-xl bg-rose-50 text-rose-500 flex items-center justify-center mb-2"
        >
          <span class="i-lucide-alert-circle text-xl" />
        </div>
        <p class="text-xs font-medium text-rose-500">
          {{ error }}
        </p>
      </div>
      <div v-else class="h-full w-full">
        <ApexCharts
          width="100%"
          height="100%"
          :type="localChartType"
          :options="isCircular ? apexPieOptions : apexChartOptions"
          :series="isCircular ? seriesData[0].data : seriesData"
        />
      </div>
    </div>
  </div>
</template>
